import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  useEdgesState,
  useNodesState,
  ReactFlowProvider,
  useReactFlow,
  applyNodeChanges,
  applyEdgeChanges,
} from "reactflow";
import "reactflow/dist/style.css";
import { getFlow, getFlows, saveFlow } from "../../../hooks/flowApi";
import NodeEditPanel from "./NodeEditPanel";
import nodeTypes from "./nodeTypes";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./BotWorkflow.css";

// Import icons
import {
  FiMessageSquare,
  FiLayers,
  FiLink,
  FiImage,
  FiCode,
  FiClock,
  FiSave,
  FiChevronDown,
  FiPlus,
  FiX,
  FiMenu,
  FiMaximize2,
  FiMinimize2,
} from "react-icons/fi";
import { v4 as uuidv4 } from 'uuid';

const getId = () => uuidv4();

const initialNodes = [
  {
    id: "1",
    type: "editableNode",
    data: {
      label: "Flow Start",
      nodeType: "start",
      onChange: () => {},
    },
    position: { x: 50, y: 50 },
  },
  {
    id: "2",
    type: "editableNode",
    data: {
      label: "Send a Message",
      nodeType: "send-a-message",
      content: "Hello, welcome to our service!",
      onChange: () => {},
    },
    position: { x: 250, y: 150 },
  },
];

const initialEdges = [{ id: "e1-2", source: "1", target: "2" }];

function FlowCanvas({ nodes, setNodes, edges, setEdges, onNodeClick }) {
  const { project } = useReactFlow();
  const memoizedNodeTypes = useMemo(() => nodeTypes, []);

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes]
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  );

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/reactflow");
      if (!type) return;

      const position = project({
        x: event.clientX - 300,
        y: event.clientY - 100,
      });

      const newNode = {
        id: getId(),
        type: "editableNode",
        position,
        data: {
          label: type,
          nodeType: type.toLowerCase().replace(/\s+/g, "-"),
          content: "",
          ...(type === "Send a Message" && {
            content: "Type your message here...",
          }),
          ...(type === "Send a Button" && {
            content: "Button Text",
            options: "",
          }),
          onChange: (newData) => {
            setNodes((nds) =>
              nds.map((node) =>
                node.id === newNode.id
                  ? { ...node, data: { ...node.data, ...newData } }
                  : node
              )
            );
          },
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [project, setNodes]
  );

  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  return (
    <div className="flow-canvas-wrapper">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeClick={onNodeClick}
        nodeTypes={memoizedNodeTypes}
        fitView
      >
        <Background color="#aaa" gap={16} />
        <Controls className="controls-panel" />
        <MiniMap className="minimap" zoomable pannable />
      </ReactFlow>
    </div>
  );
}

function SidebarItem({ label, icon }) {
  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  const getIcon = () => {
    switch (label) {
      case "Send a Message":
        return <FiMessageSquare className="sidebar-icon" />;
      case "Send a Button":
        return <FiLayers className="sidebar-icon" />;
      case "Send Media":
        return <FiImage className="sidebar-icon" />;
      case "Trigger Webhook":
        return <FiLink className="sidebar-icon" />;
      case "Delay":
        return <FiClock className="sidebar-icon" />;
      default:
        return <FiPlus className="sidebar-icon" />;
    }
  };

  return (
    <div
      className="sidebar-item"
      draggable
      onDragStart={(event) => onDragStart(event, label)}
    >
      {getIcon()}
      <span>{label}</span>
    </div>
  );
}

export default function BotWorkflow() {
  const [nodes, setNodes] = useNodesState(initialNodes);
  const [edges, setEdges] = useEdgesState(initialEdges);
  const [flowName, setFlowName] = useState("Untitled Flow");
  const [savedFlows, setSavedFlows] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const loadSavedFlows = async () => {
      try {
        const { data } = await getFlows();
        setSavedFlows(data);
      } catch (err) {
        console.error("Error loading saved flows", err);
        toast.error("Failed to load saved workflows");
      }
    };
    loadSavedFlows();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    if (id) handleLoad(id);
  }, []);

  const handleLoad = async (id) => {
    try {
      const { data } = await getFlow(id);
      const enrichedNodes = data.nodes.map((node) => ({
        ...node,
        type: "editableNode",
        data: {
          ...node.data,
          onChange: (newData) => {
            setNodes((nds) =>
              nds.map((n) =>
                n.id === node.id ? { ...n, data: { ...n.data, ...newData } } : n
              )
            );
          },
        },
      }));
      setNodes(enrichedNodes);
      setEdges(data.edges);
      setFlowName(data.name);
      setSelectedNode(null);
      toast.success(`Loaded workflow: ${data.name}`);
    } catch (err) {
      console.error("Error loading workflow", err);
      toast.error("Failed to load workflow");
    }
  };

// Enhanced error handling
const handleSave = async () => {
  try {
    if (!flowName.trim()) {
      toast.error("Please enter a workflow name");
      return;
    }
    
    if (nodes.length < 2) {
      toast.error("Workflow must have at least 2 nodes");
      return;
    }

    const payload = { 
      name: flowName.trim(), 
      nodes, 
      edges,
      // Add metadata
      updatedAt: new Date().toISOString(),
      version: 1 
    };
    
    const response = await saveFlow(payload);
    toast.success("Workflow saved successfully!");
    return response;
  } catch (error) {
    console.error("Save failed", error);
    toast.error(error.response?.data?.message || "Error saving workflow");
    throw error;
  }
};

  const handlePanelClose = () => setSelectedNode(null);
  const handleNodeClick = (event, node) => setSelectedNode(node);

  const updateNodeData = (nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...newData } }
          : node
      )
    );
  };

  const handleDeleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) =>
      eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
    );
    setSelectedNode(null);
    toast.info("Node deleted");
  };

  const handleSaveWithLoading = async () => {
  setIsSaving(true);
  try {
    await handleSave();
  } finally {
    setIsSaving(false);
  }
};


  return (
    <ReactFlowProvider>
      <div className="workflow-builder">
        {/* Header */}
        <header className="builder-header">
          <div className="header-left">
            <button
              className="sidebar-toggle"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            >
              <FiMenu />
            </button>
            <h1 className="app-title">WhatsApp Bot Builder</h1>
          </div>

          <div className="header-center">
            <input
              className="flow-name-input"
              value={flowName}
              onChange={(e) => setFlowName(e.target.value)}
              placeholder="Workflow name"
            />
          </div>

          <div className="header-right">
            <button 
  className="btn-save" 
  onClick={handleSaveWithLoading}
  disabled={isSaving}
>
  <FiSave className="icon" />
  <span>{isSaving ? "Saving..." : "Save"}</span>
</button>
          </div>
        </header>

        {/* Main Content */}
        <div className="builder-content">
          {/* Sidebar */}
          <aside
            className={`actions-sidebar ${
              isSidebarCollapsed ? "collapsed" : ""
            }`}
          >
            <div className="sidebar-header">
              <h3>Actions</h3>
              <button
                className="collapse-btn"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              >
                {isSidebarCollapsed ? <FiMaximize2 /> : <FiMinimize2 />}
              </button>
            </div>

            <div className="sidebar-items">
              <SidebarItem label="Send a Message" icon={<FiMessageSquare />} />
              <SidebarItem label="Send a Button" icon={<FiLayers />} />
              <SidebarItem label="Send Media" icon={<FiImage />} />
              <SidebarItem label="Trigger Webhook" icon={<FiLink />} />
              <SidebarItem label="Delay" icon={<FiClock />} />
            </div>
          </aside>

          {/* Canvas Area */}
          <main className="canvas-container">
            <FlowCanvas
              nodes={nodes}
              setNodes={setNodes}
              edges={edges}
              setEdges={setEdges}
              onNodeClick={handleNodeClick}
            />
          </main>

          {/* Edit Panel */}
          {selectedNode && (
            <aside className="edit-panel">
              <NodeEditPanel
                node={selectedNode}
                onClose={handlePanelClose}
                onUpdate={updateNodeData}
                onDelete={handleDeleteNode}
                edges={edges}
                onEdgeUpdate={(edgeId, newData) => {
                  setEdges((eds) =>
                    eds.map((e) => (e.id === edgeId ? newData : e))
                  );
                }}
                nodes={nodes}
              />
            </aside>
          )}
        </div>
      </div>
    </ReactFlowProvider>
  );
}
